export const TRAINING_DELIVERY_FORMATS = [
  {
    value: 'gym_together',
    label: 'Совместная в зале',
    shortLabel: 'Совместно',
    note: 'Совместная тренировка тренера и клиента',
    tone: 'amber',
  },
  {
    value: 'video_together',
    label: 'Совместная по видео',
    shortLabel: 'По видео',
    note: 'Тренировка тренера и клиента по видео',
    tone: 'sky',
  },
  {
    value: 'self_guided',
    label: 'Самостоятельно',
    shortLabel: 'Самостоятельно',
    note: 'Самостоятельная тренировка клиента без тренера',
    tone: 'slate',
  },
];

export const TRAINING_DELIVERY_FORMAT_UNSET = 'unset';

export const TRAINING_DELIVERY_FORMAT_FILTER_OPTIONS = [
  ...TRAINING_DELIVERY_FORMATS,
  {
    value: TRAINING_DELIVERY_FORMAT_UNSET,
    label: 'Формат не задан',
    shortLabel: 'Не задан',
    note: 'Формат тренировки не указан',
    tone: 'slate',
  },
];

const TRAINING_DELIVERY_FORMAT_BY_VALUE = Object.fromEntries(
  TRAINING_DELIVERY_FORMATS.map((item) => [item.value, item]),
);

import {
  isWorkoutCalendarDayKnown,
  isWorkoutFullyUndeterminedScheduleDay,
  resolveWorkoutRelativeDayNumber,
  SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
} from '~/utils/workoutScheduleContext';
import {
  formatLocalTimeFromParty,
  formatWorkoutDualScheduleTimeLabel,
  localPartsFromUtc,
  normalizeIanaTimezone,
} from '~/utils/timezone';

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function parseYmd(value) {
  const raw = String(value ?? '').slice(0, 10);
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function formatYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function resolveWorkoutCalendarDateSort(workout) {
  const fromDateSort = String(workout?.date_sort ?? '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromDateSort)) {
    return fromDateSort;
  }

  const fromEffectiveDate = String(workout?.schedule_context?.effective_date ?? '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromEffectiveDate)) {
    return fromEffectiveDate;
  }

  return '';
}

function startOfIsoWeek(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  const weekday = normalized.getDay();
  const diff = weekday === 0 ? -6 : 1 - weekday;
  normalized.setDate(normalized.getDate() + diff);
  return normalized;
}

export function resolveWorkoutDeliveryFormatKey(workout) {
  const key = String(
    workout?.delivery_format
    || workout?.training_format
    || workout?.format
    || workout?.schedule_context?.effective_delivery_format
    || '',
  ).trim();
  return TRAINING_DELIVERY_FORMAT_BY_VALUE[key] ? key : '';
}

export function resolveTrainingDeliveryFormatMeta(format) {
  const key = String(format ?? '').trim();
  return TRAINING_DELIVERY_FORMAT_BY_VALUE[key] || null;
}

export function resolveWorkoutDeliveryFormatMeta(workout) {
  return resolveTrainingDeliveryFormatMeta(resolveWorkoutDeliveryFormatKey(workout));
}

export function formatWorkoutDeliveryFormatShortLabel(workout) {
  const meta = resolveWorkoutDeliveryFormatMeta(workout);
  if (meta) {
    return meta.shortLabel;
  }

  const label = String(workout?.delivery_format_label ?? '').trim();
  return label || 'Формат не задан';
}

export function formatDeliveryFormatDotClass(tone) {
  const map = {
    amber: 'tdf-dot--amber',
    sky: 'tdf-dot--sky',
    slate: 'tdf-dot--slate',
  };
  return map[tone] || 'tdf-dot--slate';
}

export function hasWorkoutScheduleTime(workout) {
  const schedule = workout?.schedule;
  if (schedule && typeof schedule.hasTime === 'boolean') {
    return schedule.hasTime;
  }

  const scheduleContext = workout?.schedule_context;
  const time = String(
    schedule?.time
    ?? workout?.start_time
    ?? scheduleContext?.start_time_stored
    ?? scheduleContext?.effective_start_time
    ?? '',
  ).trim();
  return Boolean(time && time !== '--:--');
}

export const WORKOUT_SCHEDULE_TIME_PLACEHOLDER = '--:--';

function resolveWorkoutViewerScheduleTime(workout) {
  const scheduleContext = workout?.schedule_context;
  const timezoneContext = scheduleContext?.timezone_context;

  const viewerTime = formatLocalTimeFromParty(timezoneContext?.viewer);
  if (viewerTime) {
    return viewerTime;
  }

  const scheduledAtUtc = scheduleContext?.scheduled_at_utc;
  const viewerIana = normalizeIanaTimezone(timezoneContext?.viewer?.iana);
  if (scheduledAtUtc && viewerIana) {
    const parts = localPartsFromUtc(scheduledAtUtc, viewerIana);
    if (parts?.time) {
      return parts.time.slice(0, 5);
    }
  }

  return '';
}

export function formatWorkoutScheduleTimeDisplay(workout) {
  if (!hasWorkoutScheduleTime(workout)) {
    return '';
  }

  const viewerTime = resolveWorkoutViewerScheduleTime(workout);
  if (viewerTime) {
    return viewerTime;
  }

  const scheduleContext = workout?.schedule_context;
  const schedule = workout?.schedule;
  const time = String(
    schedule?.time
    ?? scheduleContext?.effective_start_time
    ?? workout?.start_time
    ?? '',
  ).trim();
  return time.slice(0, 5);
}

export function formatWorkoutScheduleTimeLabel(workout, { placeholder = WORKOUT_SCHEDULE_TIME_PLACEHOLDER, role } = {}) {
  if (!hasWorkoutScheduleTime(workout)) {
    return placeholder;
  }

  const effectiveRole = role === 'client' || role === 'trainer'
    ? role
    : undefined;
  const dual = formatWorkoutDualScheduleTimeLabel(workout, { role: effectiveRole });
  if (dual.primary) {
    return dual.primary;
  }

  const time = formatWorkoutScheduleTimeDisplay(workout);
  return time || placeholder;
}

export function formatWorkoutScheduleTimeTooltip(workout, role = 'trainer') {
  if (!hasWorkoutScheduleTime(workout)) {
    return 'Время не задано';
  }

  const dual = formatWorkoutDualScheduleTimeLabel(workout, { role });
  if (dual.timezonesDiffer && (dual.secondary || dual.secondaryDate)) {
    const otherLabel = role === 'trainer' ? 'У клиента' : 'У тренера';
    const secondaryParts = [dual.secondaryDate, dual.secondary].filter(Boolean);
    return `${dual.primary}. ${otherLabel}: ${secondaryParts.join(', ')}`;
  }

  if (dual.primary) {
    return dual.primary;
  }

  return formatWorkoutScheduleTimeDisplay(workout) || 'Время не задано';
}

export function buildTrainingPlanWeekOverview(workouts, { weekStartYmd } = {}) {
  let anchor = parseYmd(weekStartYmd);

  if (!anchor) {
    const datedWorkouts = (workouts || [])
      .map((workout) => ({
        workout,
        dateSort: String(workout?.date_sort ?? '').slice(0, 10),
      }))
      .filter((item) => item.dateSort)
      .sort((left, right) => left.dateSort.localeCompare(right.dateSort));

    if (datedWorkouts.length) {
      anchor = parseYmd(datedWorkouts[0].dateSort);
    }
  }

  if (!anchor) {
    return [];
  }

  const monday = startOfIsoWeek(anchor);
  const workoutsByDate = new Map();

  for (const workout of workouts || []) {
    if (!isWorkoutCalendarDayKnown(workout?.schedule_context)) {
      continue;
    }

    if (isWorkoutFullyUndeterminedScheduleDay(
      workout?.schedule_context,
      workout?.schedule?.date,
    )) {
      continue;
    }

    const dateSort = resolveWorkoutCalendarDateSort(workout);
    if (!dateSort) {
      continue;
    }
    if (!workoutsByDate.has(dateSort)) {
      workoutsByDate.set(dateSort, []);
    }
    workoutsByDate.get(dateSort).push(workout);
  }

  const days = [];
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    const dateSort = formatYmd(date);
    days.push({
      id: dateSort,
      weekday: WEEKDAY_LABELS[index],
      workouts: workoutsByDate.get(dateSort) || [],
    });
  }

  return days;
}

export function buildTrainingPlanWeekOverviewForSection(positionSection) {
  const weekStartYmd = positionSection?.weekHeaders?.[0]?.dateStartSort || '';
  return buildTrainingPlanWeekOverview(positionSection?.workouts || [], { weekStartYmd });
}

function resolvePlanRelativeDayColumnCount(relativeWorkouts) {
  let maxIndex = 0;

  for (const workout of relativeWorkouts || []) {
    const relativeDay = resolveWorkoutRelativeDayNumber(workout?.schedule_context);
    if (relativeDay !== null) {
      maxIndex = Math.max(maxIndex, relativeDay);
    }
  }

  return Math.max(1, maxIndex);
}

export function collectTrainingPlanRelativeDayWorkouts(workouts) {
  return (workouts || []).filter((workout) => (
    !isWorkoutCalendarDayKnown(workout?.schedule_context)
    && resolveWorkoutRelativeDayNumber(workout?.schedule_context) !== null
  ));
}

export function collectTrainingPlanUndeterminedDayWorkouts(workouts) {
  return (workouts || []).filter((workout) => {
    if (isWorkoutCalendarDayKnown(workout?.schedule_context)) {
      return isWorkoutFullyUndeterminedScheduleDay(
        workout?.schedule_context,
        workout?.schedule?.date,
      );
    }

    return resolveWorkoutRelativeDayNumber(workout?.schedule_context) === null;
  });
}

export function buildTrainingPlanRelativeDaysOverview(workouts) {
  const relativeWorkouts = collectTrainingPlanRelativeDayWorkouts(workouts);
  if (!relativeWorkouts.length) {
    return [];
  }

  const columnCount = resolvePlanRelativeDayColumnCount(relativeWorkouts);
  const workoutsByDay = new Map();

  for (const workout of relativeWorkouts) {
    const dayNumber = resolveWorkoutRelativeDayNumber(workout?.schedule_context);
    if (!dayNumber) {
      continue;
    }
    if (!workoutsByDay.has(dayNumber)) {
      workoutsByDay.set(dayNumber, []);
    }
    workoutsByDay.get(dayNumber).push(workout);
  }

  const days = [];
  for (let index = 1; index <= columnCount; index += 1) {
    days.push({
      id: `relative:${index}`,
      weekday: `День ${index}`,
      workouts: workoutsByDay.get(index) || [],
    });
  }

  return days;
}

export function buildTrainingPlanUndeterminedDaysOverview(workouts) {
  const undeterminedWorkouts = collectTrainingPlanUndeterminedDayWorkouts(workouts);
  if (!undeterminedWorkouts.length) {
    return [];
  }

  return undeterminedWorkouts.map((workout, index) => ({
    id: String(workout?.id ?? workout?.uuid ?? `undetermined:${index}`),
    weekday: SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
    workouts: [workout],
  }));
}
